import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AvailableDate } from '@app/services/api/api.service';
import { DateHelper } from '@app/date-helper';

@Component({
  selector: 'app-day',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './day.component.html',
  styleUrls: ['./day.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DayComponent {
  @Input() public i!: number;
  @Input() public noAvailability = false;
  @Input() public anchorStart?: number;
  @Input() public anchorStartDate?: string;
  @Output() public anchorStartDateChange = new EventEmitter<string>();
  @Input() public anchorEnd?: number;
  @Input() public anchorEndDate?: string;
  @Output() public anchorEndDateChange = new EventEmitter<string>();
  @Input() public toStartDay!: number;
  @Input() public fromStartDay!: number;
  @Input() public firstDayOfMonth!: number;
  @Input() public daysInMonth!: number;
  @Input() public toDays!: number;
  @Input() public monthOffset!: number;
  @Input() public absoluteAnchorStart?: number;
  @Input() public absoluteAnchorEnd?: number;
  @Input() public focus?: number;
  @Input() public month?: string;
  @Input() public fromMonth?: number;
  @Input() public toMonth?: number;
  @Input() public unavailableDates?: AvailableDate[];
  // @Input() public dateValue?: Date;
  @Input() public dateValue?: string;
  @Input() public selecting?: boolean;
  @Output() public selectingChange = new EventEmitter<boolean>();
  @Output() public dayClick = new EventEmitter<void>();
  @Output() public dayMouseenter = new EventEmitter<void>();
  @Output() public dayMouseleave = new EventEmitter<void>();

  @HostListener('click', ['$event'])
  public onClick(event: MouseEvent) {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      this.dayClick.emit();
    }
  }

  @HostListener('mouseenter', ['$event'])
  public onMouseenter(event: MouseEvent) {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      this.dayMouseenter.emit();
    }
  }

  @HostListener('mouseleave', ['$event'])
  public onMouseleave(event: MouseEvent) {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      this.dayMouseleave.emit();
    }
  }

  public disabled: boolean = false;
  public anchor = false;
  public edge = false;

  /** whether this day has a rounded top-left corner */
  public topLeftCorner = false;
  /** whether this day has a rounded top-right corner */
  public topRightCorner = false;
  /** whether this day has a rounded bottom-left corner */
  public bottomLeftCorner = false;
  /** whether this day has a rounded bottom-left corner */
  public bottomRightCorner = false;
  /** whether this day is at the beginning of a week, or the start anchor within a week */
  public capStart = false;
  /** whether this day is at the end of a week, or the end anchor within a week */
  public capEnd = false;

  public isAnchorStart = false;
  public isAnchorEnd = false;

  constructor(private cdr: ChangeDetectorRef) {}

  // anchor start is the first day selected, so the max anchor end is the first day after that that is occupied
  // todo use days in month instad of from days, then use for both months. use an ng-content to set the content of the cell
  // TODO use initial start and end in case it is cleared?
  ngOnChanges(changes: SimpleChanges) {
    this.disabled = false;
    this.edge = false;

    if (this.noAvailability) {
      this.disabled = true;
    }

    if (!this.disabled) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateValueObj = new Date(this.dateValue);
      dateValueObj.setHours(0, 0, 0, 0);
      if (dateValueObj < today) {
        this.disabled = true;
      }
    }

    if (
      !this.disabled &&
      this.selecting &&
      this.unavailableDates?.length &&
      this.anchorStartDate
    ) {
      if (
        this.unavailableDates.some((d) => {
          return (
            d.date < this.dateValue &&
            d.date > DateHelper.normalizeDateToDateString(this.anchorStartDate)
          );
        })
      )
        if (
          this.dateValue >
          DateHelper.normalizeDateToDateString(
            new Date(this.unavailableDates[0].date)
          )
        ) {
          this.disabled = true;
        }
    }

    if (!this.disabled && this.unavailableDates?.length) {
      for (let i = 0; i < this.unavailableDates.length; i++) {
        if (
          this.dateValue ===
          DateHelper.normalizeDateToDateString(
            new Date(this.unavailableDates[i].date)
          )
        ) {
          this.disabled = true;
          this.cdr.detectChanges();
          break;
        }
      }
    }

    if (!(changes['anchorStart'] || changes['anchorEnd'] || changes['focus'])) {
      // return;
    }

    const i = this.i;
    const anchorStart = this.anchorStart;
    const anchorEnd = this.anchorEnd;
    const absoluteAnchorStart = this.absoluteAnchorStart;
    const absoluteAnchorEnd = this.absoluteAnchorEnd;
    const monthOffset = this.monthOffset;
    const focus = this.focus;
    const firstDayOfMonth = this.firstDayOfMonth;
    const daysInMonth = this.daysInMonth;
    const month = this.month;
    const anchorStartDate = this.anchorStartDate;
    const anchorEndDate = this.anchorEndDate;
    const dateValue = this.dateValue;

    if (i == undefined) {
      return;
    }

    this.isAnchorStart = false;
    this.isAnchorEnd = false;
    if (this.anchorStartDate && this.dateValue === this.anchorStartDate) {
      this.isAnchorStart = true;
    }
    if (this.anchorEndDate && this.dateValue === this.anchorEndDate) {
      this.isAnchorEnd = true;
    }

    this.anchor = false;
    if (
      anchorStart !== undefined &&
      monthOffset !== undefined &&
      absoluteAnchorStart !== undefined
    ) {
      if (i === anchorStart) {
        this.anchor = true;
        this.isAnchorStart = true;
        // this is off by 1, it is 1 more than what is in the calendar
        this.anchorStartDateChange.emit(this.dateValue);
      } else if (i === anchorEnd) {
        this.anchor = true;
        this.isAnchorEnd = true;
        this.anchorEndDateChange.emit(this.dateValue);
      } else {
        this.anchor = false;
        this.isAnchorStart = false;
        this.isAnchorEnd = false;
      }
    }

    if (anchorStartDate && dateValue === anchorStartDate) {
      this.anchor = true;
      this.isAnchorStart = true;
      this.anchorStartDateChange.emit(this.dateValue);
    } else if (anchorEndDate && dateValue === anchorEndDate) {
      this.anchor = true;
      this.isAnchorEnd = true;
    } else {
      // this.anchor = false;
    }

    this.edge = false;

    if (
      anchorStartDate &&
      anchorEndDate &&
      new Date(dateValue) > new Date(anchorStartDate) &&
      new Date(dateValue) < new Date(anchorEndDate)
    ) {
      this.edge = true;
    }

    if (!this.edge) {
      this.edge =
        new Date(this.dateValue) < new Date(this.anchorEndDate) &&
        new Date(this.dateValue) > new Date(this.anchorStartDate);
    }

    if (
      anchorStart !== undefined ||
      (new Date(anchorStartDate) < new Date(dateValue) &&
        new Date(anchorEndDate) > new Date(dateValue))
    ) {
      const anchorEndIsAfter =
        anchorEndDate !== undefined && anchorEndDate > dateValue;
      const focusIsAfter = focus !== undefined && focus > i;

      let lastPosition;
      if (anchorEnd !== undefined) {
        if (focus !== undefined) {
          if (anchorEnd > focus) {
            lastPosition = anchorEnd;
          } else {
            lastPosition = focus;
          }
        } else {
          lastPosition = anchorEnd;
        }
      } else if (focus !== undefined) {
        if (anchorStart !== undefined) {
          lastPosition = focus;
        }
      }

      if (this.selecting) {
        if (
          new Date(this.anchorStartDate) < new Date(dateValue) &&
          focusIsAfter
        ) {
          this.edge = true;
        }
      } else {
        if (
          anchorStartDate &&
          new Date(this.anchorStartDate) < new Date(dateValue) &&
          anchorEndDate &&
          new Date(anchorEndDate) > new Date(dateValue)
        ) {
          this.edge = true;
        }
      }

      if (!this.edge && this.selecting) {
        if (
          (anchorStartDate &&
            new Date(anchorStartDate) < new Date(dateValue)) ||
          (anchorEndDate && new Date(anchorEndDate) > new Date(dateValue))
        ) {
          this.edge = true;
        }

        if (this.anchorStart !== undefined) {
          this.edge =
            (anchorStart < i &&
              (anchorEndIsAfter || focusIsAfter) &&
              absoluteAnchorStart === monthOffset) ||
            (absoluteAnchorStart! < monthOffset! &&
              focus !== undefined &&
              focus > i &&
              anchorStart < i);

          if (!this.edge) {
            if (anchorEnd && absoluteAnchorStart! < monthOffset) {
              if (lastPosition && lastPosition > i && this.anchorStart < i) {
                this.edge = true;
              }
            }
            if (absoluteAnchorEnd! > this.monthOffset) {
              if (absoluteAnchorStart! < this.monthOffset) {
              } else if (anchorStart < i && anchorEnd && anchorEnd > i) {
                if (
                  lastPosition &&
                  lastPosition > i &&
                  this.anchorEnd !== undefined &&
                  this.anchorEnd > i
                ) {
                  this.edge = true;
                }
                this.edge = true;
              }
            }
          }
        }
      }

      if (this.month === 'from') {
        this.capEnd =
          i % 7 === 0 ||
          i - firstDayOfMonth! === daysInMonth ||
          firstDayOfMonth + daysInMonth === i;
        this.capStart =
          (i - 1) % 7 === 0 ||
          i === firstDayOfMonth + 1 ||
          firstDayOfMonth + daysInMonth < i;
      } else {
        this.capEnd =
          (i - 40) % 7 === 0 || i - 40 - firstDayOfMonth! === daysInMonth;
        this.capStart =
          (i - 40 - 1) % 7 === 0 || i - 40 - 1 === firstDayOfMonth;
      }

      if (!anchorStart && !anchorEnd) {
        if (
          anchorStartDate &&
          anchorEndDate &&
          new Date(dateValue) > new Date(anchorStartDate) &&
          new Date(dateValue) < new Date(anchorEndDate) &&
          !DateHelper.isSameDay(new Date(dateValue), new Date(anchorStartDate))
        ) {
          this.edge = true;
        }
      }

      if (this.edge) {
        if (this.capStart) {
          if (this.month === 'from') {
            this.topLeftCorner =
              (((absoluteAnchorStart !== undefined &&
                absoluteAnchorStart < monthOffset &&
                i === 8) ||
                i - anchorStart < 7) &&
                lastPosition! > i) ||
              i === firstDayOfMonth! + 1;
          } else {
            this.topLeftCorner =
              lastPosition! > i && (i - 40 <= 8 || i - anchorStart < 7);
          }

          if (this.month === 'from') {
            if (lastPosition! > 40) {
              this.bottomLeftCorner = daysInMonth + firstDayOfMonth - i < 7;
            } else {
              this.bottomLeftCorner = lastPosition! - i < 7;
            }
          } else {
            this.bottomLeftCorner =
              (absoluteAnchorEnd === undefined ||
                absoluteAnchorEnd > monthOffset) &&
              (lastPosition! - i < 7 ||
                daysInMonth + 40 + firstDayOfMonth - i < 7);
            if (!this.bottomLeftCorner) {
              if (lastPosition! - i < 7) {
                this.bottomLeftCorner = true;
              }
            }
          }
          if (!this.capEnd) {
            this.topRightCorner = false;
            this.bottomRightCorner = false;
          }
        }

        if (this.capEnd) {
          if (!this.capStart) {
            this.topLeftCorner = false;
            this.bottomLeftCorner = false;
          }
          if (this.month === 'from') {
            this.topRightCorner =
              (i === 7 && absoluteAnchorStart! < monthOffset) ||
              (i - anchorStart <= 7 && lastPosition! > i);
          } else {
            this.topRightCorner =
              lastPosition! > i && (i - 40 <= 7 || i - anchorStart < 7);
          }
          if (this.month === 'from') {
            if (lastPosition! > 40) {
              this.bottomRightCorner = daysInMonth + firstDayOfMonth - i <= 7;
            } else {
              this.bottomRightCorner = lastPosition! - i < 7;
            }
          } else {
            this.bottomRightCorner =
              lastPosition! - i <= 7 ||
              (absoluteAnchorEnd !== undefined &&
                absoluteAnchorEnd > monthOffset &&
                i === firstDayOfMonth + daysInMonth + 40);

            if (!this.bottomRightCorner) {
              if (lastPosition! > i && lastPosition! - i < 7) {
                this.bottomRightCorner = true;
              }
              if (!this.bottomRightCorner) {
                if (
                  absoluteAnchorEnd !== undefined &&
                  absoluteAnchorEnd > monthOffset &&
                  daysInMonth + 40 + firstDayOfMonth - i < 7
                ) {
                  this.bottomRightCorner = true;
                }
              }
            }
          }
        }
      }

      if (!this.capStart && !this.capEnd) {
        this.topLeftCorner = false;
        this.topRightCorner = false;
        this.bottomLeftCorner = false;
        this.bottomRightCorner = false;
      }
    } else {
      this.edge = false;
      this.capStart = false;
      this.capEnd = false;
      this.topLeftCorner = false;
      this.topRightCorner = false;
      this.bottomLeftCorner = false;
      this.bottomRightCorner = false;
    }
  }
}
